import { useState } from 'react';
import { useStudent } from './useStudent';

type FilterState = {
  center_id?: number;
  course_id?: number;
  batch_id?: number;
  status?: string;
  session_id?: number;
  stage_id?: number;
};

type SortConfig = {
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
};

export const useStudentFilters = () => {
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [filters, setFilters] = useState<FilterState>({});
  const [tempFilters, setTempFilters] = useState<FilterState>({});
  const [sortConfig, setSortConfig] = useState<SortConfig>({});
  const [selectedCourseStages, setSelectedCourseStages] = useState<any[]>([]);

  const { getStudentState, paginationData } = useStudent({
    page: pageNumber,
    size: recordsPerPage,
    search,
    ...filters,
    ...sortConfig,
  });

  const handleSearchChange = (text: string) => {
    setSearch(text);
    setPageNumber(1);
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    setPageNumber(page);
    if (pageSize) {
      setRecordsPerPage(pageSize);
    }
  };

  const handleFilterChange = (filterName: string, value: any, courses?: any[]) => {
    setTempFilters(prev => ({
      ...prev,
      [filterName]: filterName === 'status' ? value?.value : (value?.value ? Number(value.value) : undefined)
    }));

    // Update stages when course changes
    if (filterName === 'course_id' && courses) {
      const selectedCourse = courses.find(course => course.id.toString() === value?.value);
      setSelectedCourseStages(selectedCourse?.courseStages || []);
      // Reset stage filter when course changes
      setTempFilters(prev => ({
        ...prev,
        stage_id: undefined
      }));
    }
  };

  const handleSortChange = (sortBy: string, order: 'asc' | 'desc') => {
    const newOrder = sortConfig.sort_by === sortBy && sortConfig.sort_order === 'asc' ? 'desc' : 'asc';
    setSortConfig({
      sort_by: sortBy,
      sort_order: newOrder
    });
    setPageNumber(1);
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      center_id: undefined,
      course_id: undefined,
      batch_id: undefined,
      status: undefined,
      session_id: undefined,
      stage_id: undefined,
    };
    setFilters(emptyFilters);
    setTempFilters(emptyFilters);
    setSortConfig({
      sort_by: undefined,
      sort_order: undefined,
    });
    setPageNumber(1);
  };

  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setPageNumber(1);
  };

  return {
    getStudentState,
    paginationData,
    search,
    pageNumber,
    recordsPerPage,
    filters,
    tempFilters,
    sortConfig,
    handleSearchChange,
    handlePageChange,
    handleFilterChange,
    handleSortChange,
    handleResetFilters,
    handleApplyFilters,
    setTempFilters,
    setRecordsPerPage,
    selectedCourseStages,
  };
}; 